// ═══ 검차 상세 본문 — 모바일 페이지(A3)와 데스크톱 우측 패널(C1 B)이 공유 (신고 371 §8 R2) ═══
// props: { id, user, layout: 'page'|'pane', onChanged }
//   layout='page' → 기존 /main/s/requests/:id 페이지 렌더(모바일 렌더 트리 무변경 원칙).
//   layout='pane' → `.ss-detail-pane` 자체가 루트다. `.ss-action-bar` 는 margin-top:auto 로 바닥에 붙으므로
//                   이 flex 컬럼의 **마지막 자식**이어야 한다(simple.css §8) — 시트·토스트는 fixed 라 무관.
//   onChanged      → decide/reject/cancel 성공 시 호출(좌 목록 재조회). 라우트 이동은 하지 않는다.
// 결정 7 — 하단 라벨은 앱 실제 라벨 evalActionDecide / evalActionReject (시트 안에서만 simpleBuy 어휘).
// 결정 6·9 — 요청 메모 번역 토글은 바이어 화면에 두지 않는다(평가사 전용). 원문만 보여준다.
// 매입 결정 성공 후에도 **이 화면에 머문다** — 차량 화면으로 이동하지 않는다(앱과 동일).
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { T, R, FONT } from '../../../theme';
import { useT, useLocale } from '../../../../i18n/I18nProvider';
import { useIsMobile } from '../../../../utils/useMobile';
import { useIsWideDesktop } from '../../useDesktop';
import { normalizePhotos } from '../../../eval/parts/OrderPhotos';
import FeeAdjust from '../../../eval/parts/FeeAdjust';
import { getOrder, decideOrder, rejectOrder, cancelOrder, reportLink } from '../../api';
import { describeSimpleError } from '../../errors';
import { fmtDate, fmtKRW, orderTitle } from '../../fmt';
import ErrorText from '../../parts/ErrorText';
import LtrText from '../../parts/LtrText';
import MoneyKRW from '../../parts/MoneyKRW';
import SimpleProgressBar from '../../parts/SimpleProgressBar';
import SimpleStatusPill from '../../parts/SimpleStatusPill';
import InspectionSections from './InspectionSections';
import RequestSource from './RequestSource';
import ResultPhotos from './ResultPhotos';
import { BuyPriceSheet, NotBuySheet, CancelDialog } from './DecisionSheets';

// 결과(사진·리포트·결정)가 노출되는 상태 — 앱 kSimpleInspectedStatuses 와 동일.
var INSPECTED = { completed: 1, decided: 1, rejected: 1 };
var CANCELABLE = { open: 1, taken: 1, inspecting: 1 };
// 결과가 아직 없는 상태 — 데스크톱 패널이 비어 보이지 않게 안내 한 줄을 깐다(D4).
var WAITING = { open: 1, taken: 1, inspecting: 1 };
// 재신청이 필요한 취소 사유 — BUILD_PLAN §3·§5.
var NEEDS_RESUBMIT = { UNIDENTIFIABLE_VEHICLE: 1, INSPECTOR_UNAVAILABLE: 1 };

var titleRow = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 13 };
var backBtn = { width: 30, height: 30, borderRadius: R.sm, border: '1px solid ' + T.bd, background: T.card,
  color: T.body, cursor: 'pointer', fontFamily: 'inherit', display: 'grid', placeItems: 'center', flex: 'none', padding: 0 };
var h2 = { fontSize: FONT.title, fontWeight: 800, color: T.ink, letterSpacing: '-.3px' };
var idStyle = { fontSize: 12.5, color: T.muted };
var col = { display: 'flex', flexDirection: 'column', gap: 12 };
var hero = { background: T.navy, color: T.card, padding: '15px 16px', borderRadius: R.card };
var heroTop = { display: 'flex', alignItems: 'flex-start', gap: 10 };
var heroTitle = { fontSize: 17, fontWeight: 800, letterSpacing: '-.35px', minWidth: 0,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
var heroPlate = { fontSize: 12, opacity: .82, marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' };
var heroRule = { border: 0, borderTop: '1px solid rgba(255,255,255,.2)', margin: '11px 0 9px' };
var heroMeta = { display: 'flex', flexWrap: 'wrap', gap: '5px 16px', fontSize: 11, opacity: .9, alignItems: 'center' };
var box = { background: T.card, border: '1px solid ' + T.bd, borderRadius: R.card, padding: '13px 14px' };
var boxHead = { fontSize: FONT.cardTitle, fontWeight: 800, color: T.ink, marginBottom: 8 };
var memoText = { fontSize: 12, color: T.body, lineHeight: 1.6, whiteSpace: 'pre-wrap' };
var btnRow = { display: 'flex', gap: 10 };
var btn = { flex: 1, height: 38, borderRadius: R.md, border: '1px solid ' + T.bd, background: T.card, color: T.body,
  fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 };
var actionBtn = { flex: 1, height: 44, borderRadius: R.md, fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
  fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 };
var actNavy = Object.assign({}, actionBtn, { background: T.navy, border: '1px solid ' + T.navy, color: T.card, flex: 1.6 });
var actGhost = Object.assign({}, actionBtn, { background: T.card, border: '1px solid ' + T.bd, color: T.body });
var actDanger = Object.assign({}, actionBtn, { background: T.danger, border: '1px solid ' + T.danger, color: T.card });
var assignNote = { fontSize: 11.5, color: T.muted, textAlign: 'center', marginBottom: 8, lineHeight: 1.5 };
var toastStyle = { position: 'fixed', insetInlineStart: 0, insetInlineEnd: 0, bottom: 96, zIndex: 9980,
  display: 'flex', justifyContent: 'center', pointerEvents: 'none' };
var toastBox = { maxWidth: 420, padding: '10px 16px', borderRadius: R.pill, background: T.ink, color: T.card,
  fontSize: 12.5, fontWeight: 600, boxShadow: '0 8px 24px rgba(15,23,42,.24)' };
var loadingStyle = { padding: '40px 0', textAlign: 'center', fontSize: 12.5, color: T.muted };
var cancelBox = { background: T.dangerTint, border: '1px solid ' + T.dangerBd, borderRadius: R.card, padding: '13px 14px' };
var cancelTitle = { fontSize: FONT.cardTitle, fontWeight: 800, color: T.danger, marginBottom: 6 };
var cancelText = { fontSize: 12.5, color: T.body, lineHeight: 1.6, margin: 0 };
var resubmitBadge = { display: 'inline-block', marginTop: 8, fontSize: 11, fontWeight: 700, color: T.warn,
  background: T.warnTint, border: '1px solid ' + T.warnBd, borderRadius: R.pill, padding: '3px 10px' };
var reapplyBtn = Object.assign({}, actionBtn, { background: T.navy, border: '1px solid ' + T.navy, color: T.card, marginTop: 10, width: '100%' });
var reapplyNote = { fontSize: 11, color: T.muted, marginTop: 6, textAlign: 'center' };

// ── 데스크톱 패널(시안 C1 B `.rpanel`) ──
var pHead = { flex: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '13px 18px',
  borderBottom: '1px solid ' + T.bd, background: T.head };
var pHeadText = { minWidth: 0, flex: 1 };
var pTitle = { fontSize: 16.5, fontWeight: 800, letterSpacing: '-.35px', color: T.ink,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
var pSub = { display: 'flex', flexWrap: 'wrap', gap: '2px 9px', fontSize: 12, color: T.muted, marginTop: 1 };
var pHeadRight = { marginInlineStart: 'auto', display: 'flex', alignItems: 'center', gap: 9, flex: 'none' };
var pSmBtn = { height: 28, padding: '0 11px', borderRadius: R.sm, border: '1px solid ' + T.bd, background: T.card,
  color: T.body, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', flex: 'none' };
var pBody = { padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 13 };
var pSplit = { display: 'grid', gridTemplateColumns: 'minmax(0,1.55fr) minmax(0,1fr)', gap: 14, alignItems: 'start' };
var pStack = { display: 'flex', flexDirection: 'column', gap: 11, minWidth: 0 };
var pBarLeft = { display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, color: T.muted, minWidth: 0 };
var pBarNote = Object.assign({}, pBarLeft, { color: T.warn, lineHeight: 1.45 });
var pBarRight = { marginInlineStart: 'auto', display: 'flex', gap: 9, flex: 'none' };
var pBtn = { height: 38, padding: '0 18px', borderRadius: R.md, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
var pGhost = Object.assign({}, pBtn, { background: T.card, border: '1px solid ' + T.bd, color: T.body });
var pNavy = Object.assign({}, pBtn, { background: T.navy, border: '1px solid ' + T.navy, color: T.card });
var pDanger = Object.assign({}, pBtn, { background: T.danger, border: '1px solid ' + T.danger, color: T.card });
var pCenter = { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 18px' };
var pNote = { fontSize: 12, color: T.muted, lineHeight: 1.5, background: T.bandBg,
  border: '1px solid ' + T.bandBd, borderRadius: R.card, padding: '10px 13px' };

function actionBarStyle(isMobile) {
  if (isMobile) {
    return { position: 'fixed', insetInlineStart: 0, insetInlineEnd: 0, bottom: 60, zIndex: 59,
      borderTop: '1px solid ' + T.bd, background: T.card, padding: '10px 14px 12px' };
  }
  return { position: 'sticky', bottom: 0, marginTop: 14, borderTop: '1px solid ' + T.bd,
    background: T.card, padding: '12px 0 12px' };
}

/// 리포트 공개 링크에 화면 언어를 붙인다 — 서버 /report/:token 이 ?lang > Accept-Language 순으로 본다.
export function reportUrlWithLang(url, locale) {
  var u = String(url || '');
  return u + (u.indexOf('?') >= 0 ? '&' : '?') + 'lang=' + locale;
}

function DetailBody(props) {
  var t = useT();
  var loc = useLocale();
  var navigate = useNavigate();
  var isMobile = useIsMobile();
  var isWide = useIsWideDesktop();
  var pane = props.layout === 'pane';
  var id = props.id;
  var onChanged = props.onChanged;
  var stO = useState(null); var order = stO[0], setOrder = stO[1];
  var stL = useState(true); var loading = stL[0], setLoading = stL[1];
  var stE = useState(''); var loadErr = stE[0], setLoadErr = stE[1];
  var stA = useState(''); var actionErr = stA[0], setActionErr = stA[1];
  var stT = useState(''); var toast = stT[0], setToast = stT[1];
  var stB = useState(false); var busy = stB[0], setBusy = stB[1];
  var stS = useState(''); var sheet = stS[0], setSheet = stS[1];

  var load = useCallback(function () {
    return getOrder(id).then(function (body) {
      setOrder((body && body.data) || null);
      setLoadErr('');
      setLoading(false);
    }, function (e) {
      setLoadErr(describeSimpleError(t, e));
      setLoading(false);
    });
  // setter 3종은 useState 가 보장하는 안정 참조 — 배열 구조분해라 린터가 못 알아본다.
  }, [id, t, setOrder, setLoadErr, setLoading]);

  useEffect(function () { load(); }, [load]);

  useEffect(function () {
    if (!toast) return undefined;
    var timer = setTimeout(function () { setToast(''); }, 3000);
    return function () { clearTimeout(timer); };
  }, [toast, setToast]);

  var run = function (promise, doneMsg) {
    setBusy(true);
    setActionErr('');
    promise.then(function () {
      setSheet('');
      setBusy(false);
      setToast(doneMsg);
      load();
      if (onChanged) onChanged();
    }, function (e) {
      setSheet('');
      setBusy(false);
      setActionErr(describeSimpleError(t, e));
    });
  };

  // 매입가는 클라가 >0 을 강제한다 (서버 미검증 — BUILD_PLAN 6).
  var onDecide = function (won) {
    if (!(won > 0)) return;
    run(decideOrder(id, { purchase_price: won }), t('evalDecideDone'));
  };
  // 사유는 항상 한국어 고정값 (DecisionSheets.reasonKo).
  var onReject = function (ko) { run(rejectOrder(id, ko), t('evalRejectDone')); };
  var onCancel = function (payload) { run(cancelOrder(id, payload), t('evalCancelDone')); };

  // 다시 신청 — 차종·차량번호만 이어받는다(query 프리필, BUILD_PLAN §5·§6).
  var reapply = function () {
    var params = new URLSearchParams();
    if (order.car_model) params.set('model', order.car_model);
    if (order.car_number) params.set('plate', order.car_number);
    var qs = params.toString();
    navigate('/main/s/request' + (qs ? '?' + qs : ''));
  };

  var withReport = function (use) {
    setActionErr('');
    reportLink(id).then(function (body) {
      use(reportUrlWithLang(body && body.url, loc.locale));
    }, function (e) { setActionErr(describeSimpleError(t, e)); });
  };
  var openReport = function () {
    withReport(function (url) { window.open(url, '_blank', 'noopener'); });
  };
  var shareReport = function () {
    withReport(function (url) {
      if (navigator.share) { navigator.share({ url: url }).catch(function () { /* 사용자 취소 */ }); return; }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () { setToast(t('inspListingCopied')); }, function () { setActionErr(t('errorGeneric')); });
        return;
      }
      setActionErr(t('errorGeneric'));
    });
  };

  if (loading) {
    var spin = <div style={loadingStyle}>{t('commonLoading')}</div>;
    return pane ? <div className="ss-detail-pane">{spin}</div> : spin;
  }
  if (!order) {
    var failed = (<div>
      <ErrorText>{loadErr || t('errorGeneric')}</ErrorText>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        <button type="button" data-action="retry" style={Object.assign({}, btn, { flex: 'none', padding: '0 16px' })}
          onClick={function () { setLoading(true); load(); }}>{t('actionRetry')}</button>
        <button type="button" style={Object.assign({}, btn, { flex: 'none', padding: '0 16px' })}
          onClick={function () { navigate('/main/s/requests'); }}>{t('shellTabEval')}</button>
      </div>
    </div>);
    return pane ? <div className="ss-detail-pane"><div style={pCenter}>{failed}</div></div> : failed;
  }

  var result = order.eval_result && !order.eval_result._pending ? order.eval_result : null;
  var inspected = !!INSPECTED[order.status] && !!result;
  var photos = inspected ? normalizePhotos(result) : [];
  var status = order.status;

  // 검차비 조정 — open(평가사 배정 전)만. 성공·409 모두 상세를 다시 읽고 좌 목록도 갱신한다.
  var refreshFee = function () { load(); if (onChanged) onChanged(); };
  var feeAdjust = status === 'open' ? (<FeeAdjust order={order} fmt={fmtKRW} t={t}
    describe={function (e) { return describeSimpleError(t, e); }} onChanged={refreshFee} onNotOpen={refreshFee} />) : null;

  var sheets = (<React.Fragment>
    <BuyPriceSheet open={sheet === 'buy'} busy={busy} onClose={function () { setSheet(''); }} onConfirm={onDecide} />
    <NotBuySheet open={sheet === 'notbuy'} busy={busy} onClose={function () { setSheet(''); }} onConfirm={onReject} />
    <CancelDialog open={sheet === 'cancel'} busy={busy} assigned={status === 'taken' || status === 'inspecting'}
      onClose={function () { setSheet(''); }} onConfirm={onCancel} />
    {toast ? <div style={toastStyle}><div role="status" style={toastBox}>{toast}</div></div> : null}
  </React.Fragment>);

  // 패널 전용 — 평가사 메모 4종은 InspectionSections 가 맡으므로 여기엔 요청 메모만 남는다(§9-3).
  var notesBlocks = order.notes ? (<div style={box}>
    <div style={boxHead}>{t('inspectorOrderNotesSection')}</div>
    <p style={memoText}>{order.notes}</p>
  </div>) : null;

  // 취소 사유 카드 — cancel_reason_code 없으면(구 데이터·서버 미배포) 생략(역호환). 모바일/데스크톱 공유.
  var cancelInfo = status === 'cancelled' && order.cancel_reason_code ? (<div style={cancelBox}>
    <div style={cancelTitle}>{t('cancelReasonTitle')}</div>
    <p style={cancelText}>{t('evalCancelReason_' + order.cancel_reason_code)}</p>
    {NEEDS_RESUBMIT[order.cancel_reason_code] ? (<React.Fragment>
      <span style={resubmitBadge}>{t('cancelReasonResubmitHint')}</span>
      <button type="button" data-action="reapply" style={reapplyBtn} onClick={reapply}>{t('cancelReasonReapply')}</button>
      <p style={reapplyNote}>{t('cancelReasonReapplyNote')}</p>
    </React.Fragment>) : null}
  </div>) : null;

  if (pane) {
    // 결과가 없으면 진행바 + 신청 근거만 남아 패널이 비어 보인다 — 왜 비었는지 한 줄로 알린다(D4, 패널 전용).
    var note = inspected ? '' : WAITING[status] ? t('simpleProgWaiting') : status === 'completed' ? t('commonNoResults') : '';
    // 시안 C1 B — 좌 사진 그리드(≥1200 4열 / 1024~1199 3열) + 우 신청 근거·메모.
    var info = (<div style={pStack}>
      {note ? <div data-note="result" style={pNote}>{note}</div> : null}
      {cancelInfo}
      <RequestSource order={order} onToast={setToast} />
      {notesBlocks}
    </div>);
    // 우 열에 실제로 그릴 게 없으면(링크·요청사진·메모·취소사유 전부 없음) 2열을 만들지 않는다 —
    // 빈 열이 폭의 40% 를 먹어 사진 카드가 좁아진다. RequestSource 는 둘 다 없으면 null 을 준다.
    var hasInfo = !!note || !!cancelInfo || !!String(order.listing_url || '').trim()
      || (Array.isArray(order.request_photos) && order.request_photos.length > 0)
      || !!order.notes;
    return (<div className="ss-detail-pane ss-request-detail-pane">
      <div style={pHead}>
        <div style={pHeadText}>
          <div style={pTitle}>{orderTitle(t, order)}</div>
          <div style={pSub}>
            {order.car_number ? <LtrText>{order.car_number}</LtrText> : null}
            {order.car_year ? <LtrText>{order.car_year}</LtrText> : null}
            {order.car_mileage ? <LtrText>{Number(order.car_mileage).toLocaleString('en-US') + ' km'}</LtrText> : null}
            {order.location ? <span>{order.location}</span> : null}
            <LtrText>{fmtDate(loc.locale, order.inspect_date || order.created_at)}</LtrText>
          </div>
        </div>
        <span style={pHeadRight}>
          <SimpleStatusPill status={status} />
          {inspected ? (<React.Fragment>
            <button type="button" data-action="report" style={pSmBtn} onClick={openReport}>{t('simpleViewReport')}</button>
            <button type="button" data-action="share" style={pSmBtn} onClick={shareReport}>{t('simpleShare')}</button>
          </React.Fragment>) : null}
        </span>
      </div>

      <div style={pBody}>
        <SimpleProgressBar status={status} />
        {feeAdjust}
        {inspected ? <InspectionSections result={result} orderId={id} locale={loc.locale} /> : null}
        {inspected
          ? (hasInfo
            ? (<div className="ss-detail-split" style={pSplit}><ResultPhotos photos={photos} cols={isWide ? 4 : 3} />{info}</div>)
            : <ResultPhotos photos={photos} cols={isWide ? 4 : 3} />)
          : info}
        <ErrorText>{actionErr}</ErrorText>
      </div>

      {status === 'completed' ? (<div className="ss-action-bar">
        <span style={pBarLeft}>{t('simpleFeeLabel')} <MoneyKRW won={order.fee} /></span>
        <span style={pBarRight}>
          <button type="button" data-action="reject" style={pGhost} disabled={busy}
            onClick={function () { setSheet('notbuy'); }}>{t('evalActionReject')}</button>
          <button type="button" data-action="decide" style={pNavy} disabled={busy}
            onClick={function () { setSheet('buy'); }}>{t('evalActionDecide')}</button>
        </span>
      </div>) : null}

      {CANCELABLE[status] ? (<div className="ss-action-bar">
        {status === 'open' ? null : <span style={pBarNote}>{t('simpleCancelAssignedNote')}</span>}
        <span style={pBarRight}>
          <button type="button" data-action="cancel" style={status === 'open' ? pGhost : pDanger} disabled={busy}
            onClick={function () { setSheet('cancel'); }}>{t('evalActionCancel')}</button>
        </span>
      </div>) : null}

      {sheets}
    </div>);
  }

  return (<div className="ss-request-detail">
    <div style={titleRow}>
      <button type="button" style={backBtn} aria-label={t('actionBack')} onClick={function () { navigate('/main/s/requests'); }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7" /></svg>
      </button>
      <h2 style={h2}>{t('simpleResultsTitle')}</h2>
      <LtrText style={idStyle}>{order.order_no || order.id}</LtrText>
    </div>

    <div style={col}>
      <div style={hero}>
        <div style={heroTop}>
          <div style={{ minWidth: 0 }}>
            <div style={heroTitle}>{order.car_model || t('inspListingTitle')}</div>
            <div style={heroPlate}>
              {order.car_number ? <LtrText>{order.car_number}</LtrText> : null}
              {order.car_year ? <LtrText>{order.car_year}</LtrText> : null}
              {order.car_mileage ? <LtrText>{Number(order.car_mileage).toLocaleString('en-US') + ' km'}</LtrText> : null}
            </div>
          </div>
          <span style={{ marginInlineStart: 'auto' }}><SimpleStatusPill status={status} /></span>
        </div>
        <hr style={heroRule} />
        <div style={heroMeta}>
          <LtrText>{fmtDate(loc.locale, order.inspect_date || order.created_at)}</LtrText>
          {order.location ? <span>{order.location}</span> : null}
          <span>{t('evalTableColInspector')} <b>{order.inspector_name || '-'}</b></span>
          <span>{t('simpleFeeLabel')} <MoneyKRW won={order.fee} /></span>
        </div>
      </div>

      <div style={box}><SimpleProgressBar status={status} /></div>

      {feeAdjust}

      {cancelInfo}

      <RequestSource order={order} onToast={setToast} />

      {inspected ? (<ResultPhotos photos={photos} />) : null}

      {inspected && result.inspector_notes ? (<div style={box}>
        <div style={boxHead}>{t('evalNotesTitle')}</div>
        <p style={memoText}>{result.inspector_notes}</p>
      </div>) : null}

      {inspected ? (<div style={btnRow}>
        <button type="button" data-action="report" style={btn} onClick={openReport}>{t('simpleViewReport')}</button>
        <button type="button" data-action="share" style={btn} onClick={shareReport}>{t('simpleShare')}</button>
      </div>) : null}

      {order.notes ? (<div style={box}>
        <div style={boxHead}>{t('inspectorOrderNotesSection')}</div>
        <p style={memoText}>{order.notes}</p>
      </div>) : null}
    </div>

    <ErrorText>{actionErr}</ErrorText>

    {status === 'completed' ? (<div style={actionBarStyle(isMobile)}>
      <div style={btnRow}>
        <button type="button" data-action="reject" style={actGhost} disabled={busy}
          onClick={function () { setSheet('notbuy'); }}>{t('evalActionReject')}</button>
        <button type="button" data-action="decide" style={actNavy} disabled={busy}
          onClick={function () { setSheet('buy'); }}>{t('evalActionDecide')}</button>
      </div>
    </div>) : null}

    {CANCELABLE[status] ? (<div style={actionBarStyle(isMobile)}>
      {status === 'open' ? null : <div style={assignNote}>{t('simpleCancelAssignedNote')}</div>}
      <div style={btnRow}>
        <button type="button" data-action="cancel" style={status === 'open' ? actGhost : actDanger} disabled={busy}
          onClick={function () { setSheet('cancel'); }}>{t('evalActionCancel')}</button>
      </div>
    </div>) : null}

    {isMobile && (status === 'completed' || CANCELABLE[status]) ? <div style={{ height: 76 }} /> : null}

    {sheets}
  </div>);
}

export default DetailBody;
